import React, { useState } from "react";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import Icon from "components/AppIcon";

const PortfolioSummary = ({ holdings, totalValue, dailyPnl, weeklyPnl }) => {
  const [view, setView] = useState("daily");

  const safeHoldings = Array.isArray(holdings) ? holdings : [];
  const safeDailyPnl = Array.isArray(dailyPnl) ? dailyPnl : [];
  const safeWeeklyPnl = Array.isArray(weeklyPnl) ? weeklyPnl : [];
  const pnlData = view === "daily" ? safeDailyPnl : safeWeeklyPnl;
  const safeTotalValue = typeof totalValue === 'number' && !isNaN(totalValue) ? totalValue : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const val = payload?.[0]?.value;
      return (
        <div className="bg-card border border-border rounded px-2 py-1">
          <p className="font-caption text-xs text-muted-foreground">{label}</p>
          <p
            className="font-caption text-xs font-medium"
            style={{ color: val >= 0 ? "var(--color-success)" : "var(--color-error)" }}
          >
            {val >= 0 ? "+" : ""}${Math.abs(val)?.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  const pnlTotal = pnlData?.reduce((a, b) => a + (b?.value || 0), 0);

  return (
    <div
      className="rounded-xl border border-border p-4 md:p-5"
      style={{ background: "var(--color-card)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-sm font-semibold text-foreground">Portfolio Performance</h3>
        <div className="flex gap-1">
          {["daily", "weekly"]?.map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`font-caption text-xs px-2.5 py-1 rounded transition-all duration-200 ${
                view === v
                  ? "bg-primary/15 text-primary border border-primary/30" :"text-muted-foreground hover:text-foreground"
              }`}
            >
              {v?.charAt(0)?.toUpperCase() + v?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-4 mb-4">
        <div>
          <p className="font-caption text-xs text-muted-foreground">Portfolio Value</p>
          <p className="font-heading text-lg font-bold text-foreground">
            ${safeTotalValue?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <p className="font-caption text-xs text-muted-foreground">
            {view === "daily" ? "Today" : "This Week"}
          </p>
          <p
            className="font-heading text-lg font-bold"
            style={{
              color: pnlTotal >= 0 ? "var(--color-success)" : "var(--color-error)",
            }}
          >
            {pnlTotal >= 0 ? "+" : ""}${Math.abs(pnlTotal)?.toLocaleString()}
          </p>
        </div>
      </div>
      {pnlData?.length > 0 && (
        <div className="h-28 w-full mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={pnlData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {pnlData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry?.value >= 0 ? "var(--color-success)" : "var(--color-error)"}
                    opacity={0.8}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* Top Holdings */}
      <div className="mt-2 space-y-2">
        <p className="font-caption text-xs text-muted-foreground uppercase tracking-widest">Top Holdings</p>
        {safeHoldings?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 gap-2">
            <Icon name="Briefcase" size={28} color="var(--color-muted-foreground)" />
            <p className="font-caption text-xs text-muted-foreground text-center">
              No holdings yet — start trading
            </p>
          </div>
        ) : (
          safeHoldings?.slice(0, 3)?.map((h) => (
            <div key={h?.ticker} className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 font-heading text-xs font-bold"
                  style={{ background: "rgba(0,217,255,0.1)", color: "var(--color-primary)" }}
                >
                  {(h?.ticker || 'N/A')?.slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <p className="font-heading text-xs font-semibold text-foreground truncate">{h?.ticker || 'N/A'}</p>
                  <p className="font-caption text-xs text-muted-foreground">{h?.shares || 0} shares</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-heading text-xs font-semibold text-foreground">
                  ${(h?.value || 0)?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </p>
                <p
                  className="font-caption text-xs"
                  style={{ color: (h?.change || 0) >= 0 ? "var(--color-success)" : "var(--color-error)" }}
                >
                  {(h?.change || 0) >= 0 ? "+" : ""}{(h?.change || 0)?.toFixed(2)}%
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PortfolioSummary;